import { XMarkIcon } from "@heroicons/react/24/outline";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { ServiceTitanInvoiceLink } from "@/core/components/service-titan-invoice-link";
import { ServiceTitanJobLink } from "@/core/components/service-titan-job-link";
import { Badge } from "@/core/components/ui/badge";
import { Button } from "@/core/components/ui/button";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Separator } from "@/core/components/ui/separator";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { formatMoney } from "@/core/utils/money";
import type { IcmJob } from "@/icm/types";
import {
  getPayBreakdownStatementPath,
  PAY_BREAKDOWN_PATH,
} from "@/payroll/pay-breakdown/routes";

interface IcmJobSidebarProps {
  job: IcmJob;
  periodStart?: string;
  onClose: () => void;
}

export const IcmJobSidebar = ({
  job,
  periodStart,
  onClose,
}: IcmJobSidebarProps) => {
  const navigate = useNavigate();

  return (
    <Stack fullWidth gap="none" className="h-full">
      <Stack gap="lg" className="bg-bg-secondary/50 p-6" fullWidth>
        <Stack direction="row" justify="between" align="start" fullWidth>
          <Stack gap="xs" className="py-5">
            <Stack direction="row" align="center" gap="sm">
              <Typography variant="titleW3">
                {job.customer_name ?? "-"}
              </Typography>
              {job.job_recall_for_id && (
                <Badge
                  variant="outline"
                  className="border-orange-300 bg-orange-50 text-xs text-orange-600"
                >
                  Callback
                </Badge>
              )}
            </Stack>
            {job.job_id ? (
              <ServiceTitanJobLink jobId={job.job_id} />
            ) : (
              job.invoice_id && (
                <ServiceTitanInvoiceLink invoiceId={job.invoice_id} />
              )
            )}
          </Stack>
          <XMarkIcon
            className="h-6 w-6 shrink-0 cursor-pointer"
            onClick={onClose}
          />
        </Stack>

        <Stack gap="md" fullWidth>
          <Stack direction="row" gap="xl" fullWidth>
            {job.job_completed_on && (
              <Stack gap="xs" className="w-[50%]">
                <Typography variant="subtitleW3" textColor="tertiary">
                  Completed
                </Typography>
                <Typography variant="bodyW2">
                  {dayjs(job.job_completed_on).format("MMM DD, YYYY")}
                </Typography>
              </Stack>
            )}
            <Stack gap="xs" className="w-[50%]">
              <Typography variant="subtitleW3" textColor="tertiary">
                Job Total
              </Typography>
              <Typography variant="bodyW2">
                {job.job_total != null
                  ? formatMoney(job.job_total.toFixed(2))
                  : "-"}
              </Typography>
            </Stack>
          </Stack>
          {job.business_unit_name && (
            <Stack gap="xs">
              <Typography variant="subtitleW3" textColor="tertiary">
                Business Unit
              </Typography>
              <Typography variant="bodyW2">{job.business_unit_name}</Typography>
            </Stack>
          )}
        </Stack>
      </Stack>

      <Separator />

      <ScrollArea className="w-full flex-1">
        <Stack gap="none" fullWidth className="px-6 py-2">
          {job.payouts.map((payout, i) => (
            <Stack key={i} gap="none" fullWidth>
              <Stack
                direction="row"
                justify="between"
                align="center"
                fullWidth
                className="py-3"
              >
                <Stack gap="xs">
                  <Button
                    variant="link"
                    size="xs"
                    className="h-auto p-0 text-sm font-normal"
                    onClick={() =>
                      navigate(
                        periodStart
                          ? getPayBreakdownStatementPath(
                              periodStart,
                              payout.statement_id,
                            )
                          : `${PAY_BREAKDOWN_PATH}/statements/${payout.statement_id}`,
                      )
                    }
                  >
                    {payout.technician_name ?? "-"}
                  </Button>
                  <Typography variant="bodyW2" textColor="secondary">
                    {payout.payout_rule_name}
                  </Typography>
                </Stack>
                <Typography variant="bodyW2">
                  {formatMoney(payout.payout_amount)}
                </Typography>
              </Stack>
              <Separator />
            </Stack>
          ))}
          <Stack
            direction="row"
            justify="between"
            align="center"
            fullWidth
            className="py-3"
          >
            <Typography variant="subtitleW3" textColor="secondary">
              Total Pay
            </Typography>
            <Typography variant="subtitleW2">
              {formatMoney(job.total_payout)}
            </Typography>
          </Stack>
        </Stack>
      </ScrollArea>
    </Stack>
  );
};
